/**
 * Webtech HR Engine™ - Executive View Page
 * Vue Direction - Dashboard synthétique pour DG
 * Résumé exécutif en une page pour la prise de décision
 */

import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import DashboardLayout from '../components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Skeleton } from '../components/ui/skeleton';
import { useAuth } from '../contexts/AuthContext';
import { usePlan } from '../contexts/PlanContext';
import FeatureGate from '../components/FeatureGate';
import api from '../lib/api';
import { toast } from 'sonner';
import {
  Users,
  Building2,
  TrendingUp,
  TrendingDown,
  AlertTriangle,
  CheckCircle2,
  Brain,
  Target,
  Award,
  Briefcase,
  ArrowRight,
  Download,
  Printer,
  Share2,
  Eye,
  Shield,
  Activity,
  Zap,
  BarChart3,
  PieChart,
  Loader2,
  Clock,
  Calendar,
  ChevronRight,
  Sparkles,
  FileText,
  ArrowUpRight,
  Crown,
  Lightbulb
} from 'lucide-react';

// Radar Chart Component (Simple SVG implementation)
function RadarChart({ data, size = 200 }) {
  const categories = data || [
    { label: 'Performance', value: 0 },
    { label: 'Compétences', value: 0 },
    { label: 'Engagement', value: 0 },
    { label: 'Rétention', value: 0 },
    { label: 'Formation', value: 0 },
  ];

  const centerX = size / 2;
  const centerY = size / 2;
  const radius = (size / 2) - 30;
  const angleStep = (2 * Math.PI) / categories.length;

  // Generate polygon points for the data
  const dataPoints = categories.map((cat, i) => {
    const angle = i * angleStep - Math.PI / 2;
    const r = (cat.value / 100) * radius;
    return {
      x: centerX + r * Math.cos(angle),
      y: centerY + r * Math.sin(angle),
    };
  });

  // Generate polygon points for the grid levels
  const gridLevels = [0.25, 0.5, 0.75, 1];
  
  const getGridPoints = (level) => {
    return categories.map((_, i) => {
      const angle = i * angleStep - Math.PI / 2;
      const r = level * radius;
      return `${centerX + r * Math.cos(angle)},${centerY + r * Math.sin(angle)}`;
    }).join(' ');
  };

  // Generate axis lines
  const axisLines = categories.map((_, i) => {
    const angle = i * angleStep - Math.PI / 2;
    return {
      x2: centerX + radius * Math.cos(angle),
      y2: centerY + radius * Math.sin(angle),
    };
  });

  // Generate labels
  const labels = categories.map((cat, i) => {
    const angle = i * angleStep - Math.PI / 2;
    const labelRadius = radius + 20;
    return {
      x: centerX + labelRadius * Math.cos(angle),
      y: centerY + labelRadius * Math.sin(angle),
      label: cat.label,
      value: cat.value,
    };
  });

  return (
    <svg width={size} height={size} className="mx-auto">
      {/* Grid */}
      {gridLevels.map((level, i) => (
        <polygon
          key={i}
          points={getGridPoints(level)}
          fill="none"
          stroke="#e2e8f0"
          strokeWidth="1"
        />
      ))}
      
      {/* Axis lines */}
      {axisLines.map((line, i) => (
        <line
          key={i}
          x1={centerX}
          y1={centerY}
          x2={line.x2}
          y2={line.y2}
          stroke="#e2e8f0"
          strokeWidth="1"
        />
      ))}
      
      {/* Data polygon */}
      <polygon
        points={dataPoints.map(p => `${p.x},${p.y}`).join(' ')}
        fill="rgba(32, 84, 166, 0.2)"
        stroke="#2054A6"
        strokeWidth="2"
      />
      
      {/* Data points */}
      {dataPoints.map((point, i) => (
        <circle
          key={i}
          cx={point.x}
          cy={point.y}
          r="4"
          fill="#2054A6"
          stroke="white"
          strokeWidth="2"
        />
      ))}
      
      {/* Labels */}
      {labels.map((label, i) => (
        <text
          key={i}
          x={label.x}
          y={label.y}
          textAnchor="middle"
          dominantBaseline="middle"
          className="text-[10px] fill-slate-600 font-medium"
        >
          {label.label}
        </text>
      ))}
    </svg>
  );
}

// Gauge Component for Health Score
function HealthGauge({ score, size = 160 }) {
  const radius = (size / 2) - 15;
  const circumference = radius * Math.PI;
  const progress = (score / 100) * circumference;
  
  const getColor = (value) => {
    if (value >= 70) return '#22c55e';
    if (value >= 50) return '#3b82f6';
    if (value >= 30) return '#f59e0b';
    return '#ef4444';
  };

  const getLabel = (value) => {
    if (value >= 70) return 'Excellent';
    if (value >= 50) return 'Correct';
    if (value >= 30) return 'À surveiller';
    return 'Critique';
  };

  return (
    <div className="relative inline-flex items-center justify-center">
      <svg width={size} height={size / 2 + 20} className="transform -rotate-0">
        {/* Background arc */}
        <path
          d={`M ${15} ${size / 2} A ${radius} ${radius} 0 0 1 ${size - 15} ${size / 2}`}
          fill="none"
          stroke="#e2e8f0"
          strokeWidth="12"
          strokeLinecap="round"
        />
        {/* Progress arc */}
        <path
          d={`M ${15} ${size / 2} A ${radius} ${radius} 0 0 1 ${size - 15} ${size / 2}`}
          fill="none"
          stroke={getColor(score)}
          strokeWidth="12"
          strokeLinecap="round"
          strokeDasharray={`${progress} ${circumference}`}
          className="transition-all duration-1000 ease-out"
        />
      </svg>
      <div className="absolute bottom-0 text-center">
        <p className="text-3xl font-bold" style={{ color: getColor(score) }}>{score}%</p>
        <p className="text-xs text-slate-500 font-medium">{getLabel(score)}</p>
      </div>
    </div>
  );
}

// Mini Bar Chart Component
function MiniBarChart({ data }) {
  const maxValue = Math.max(...data.map(d => d.value), 1);
  
  return (
    <div className="flex items-end gap-1 h-16">
      {data.map((item, i) => (
        <div key={i} className="flex-1 flex flex-col items-center gap-1">
          <div 
            className="w-full rounded-t transition-all duration-500"
            style={{ 
              height: `${(item.value / maxValue) * 100}%`,
              backgroundColor: item.color,
              minHeight: '4px'
            }}
          />
          <span className="text-[9px] text-slate-500 truncate w-full text-center">
            {item.label}
          </span>
        </div>
      ))}
    </div>
  );
}

// Executive KPI Card with animation
function ExecutiveKPI({ label, value, subValue, icon: Icon, trend, color, delay = 0 }) {
  const [animated, setAnimated] = useState(false);
  
  useEffect(() => {
    const timer = setTimeout(() => setAnimated(true), delay);
    return () => clearTimeout(timer);
  }, [delay]);

  return (
    <div className={`
      bg-white rounded-xl border border-slate-200 p-4 
      transform transition-all duration-500 ease-out
      ${animated ? 'translate-y-0 opacity-100' : 'translate-y-4 opacity-0'}
      hover:shadow-lg hover:border-webtech-blue/30
    `}>
      <div className="flex items-start justify-between">
        <div className={`w-10 h-10 rounded-lg ${color} flex items-center justify-center`}>
          <Icon className="w-5 h-5 text-white" />
        </div>
        {trend && (
          <div className={`flex items-center gap-1 text-xs font-medium ${trend > 0 ? 'text-green-600' : 'text-red-600'}`}>
            {trend > 0 ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
            {Math.abs(trend)}%
          </div>
        )}
      </div>
      <p className="text-2xl font-bold text-slate-900 mt-3">{value}</p>
      <p className="text-sm text-slate-500">{label}</p>
      {subValue && <p className="text-xs text-slate-400 mt-1">{subValue}</p>}
    </div>
  );
}

// Risk Item Component
function RiskItem({ risk, severity, action }) {
  const severityConfig = {
    high: { color: 'bg-red-100 text-red-700 border-red-200', icon: AlertTriangle },
    medium: { color: 'bg-amber-100 text-amber-700 border-amber-200', icon: AlertTriangle },
    low: { color: 'bg-blue-100 text-blue-700 border-blue-200', icon: Activity },
  };
  
  const config = severityConfig[severity] || severityConfig.medium;
  const Icon = config.icon;

  return (
    <div className={`p-3 rounded-lg border ${config.color} flex items-start gap-3`}>
      <Icon className="w-4 h-4 mt-0.5 flex-shrink-0" />
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium">{risk}</p>
        {action && <p className="text-xs mt-1 opacity-80">→ {action}</p>}
      </div>
    </div>
  );
}

// Skeleton Loader for Executive View
function ExecutiveViewSkeleton() {
  return (
    <div className="space-y-6 animate-pulse">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        {[1, 2, 3, 4].map(i => (
          <Skeleton key={i} className="h-32 rounded-xl" />
        ))}
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Skeleton className="h-64 rounded-xl lg:col-span-2" />
        <Skeleton className="h-64 rounded-xl" />
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Skeleton className="h-48 rounded-xl" />
        <Skeleton className="h-48 rounded-xl" />
      </div>
    </div>
  );
}

export default function ExecutiveViewPage() {
  const { user } = useAuth();
  const { isPro, isSuperAdmin } = usePlan();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [stats, setStats] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [aiRes, statsRes] = await Promise.all([
        api.get('/ai/dashboard'),
        api.get('/dashboard/stats')
      ]);
      setData(aiRes.data);
      setStats(statsRes.data);
    } catch (error) {
      console.error('Error fetching executive data:', error);
      toast.error('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  };

  const [exporting, setExporting] = useState(false);

  const handleExport = async () => {
    setExporting(true);
    toast.info('Export PDF en préparation...');
    
    try {
      const response = await api.get('/ai/executive-view/pdf', {
        responseType: 'blob'
      });
      
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      const date = new Date().toISOString().split('T')[0].replace(/-/g, '');
      link.download = `Vue_Direction_${date}.pdf`;
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success('📊 Rapport Vue Direction téléchargé !');
    } catch (error) {
      console.error('Error exporting PDF:', error);
      toast.error('Erreur lors de l\'export PDF');
    } finally {
      setExporting(false);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const analysis = data?.latestTeamAnalysis;
  const healthScore = analysis?.globalHealthScore || 0;
  const hrKPIs = analysis?.hrKPIs || {};

  // Prepare radar chart data
  const radarData = [
    { label: 'Performance', value: analysis?.rawData?.avgScore || 0 },
    { label: 'Compétences', value: hrKPIs.skillReadiness === 'élevé' ? 80 : hrKPIs.skillReadiness === 'moyen' ? 50 : 30 },
    { label: 'Engagement', value: hrKPIs.engagementLevel === 'élevé' ? 80 : hrKPIs.engagementLevel === 'moyen' ? 50 : 30 },
    { label: 'Rétention', value: hrKPIs.turnoverRisk === 'faible' ? 80 : hrKPIs.turnoverRisk === 'moyen' ? 50 : 30 },
    { label: 'Effectif', value: stats?.employees > 0 ? 70 : 20 },
  ];

  // Performance distribution for bar chart
  const performanceData = [
    { label: 'Excel.', value: analysis?.performanceDistribution?.excellence || 0, color: '#22c55e' },
    { label: 'Prog.', value: analysis?.performanceDistribution?.progression || 0, color: '#3b82f6' },
    { label: 'Stab.', value: analysis?.performanceDistribution?.stagnation || 0, color: '#f59e0b' },
    { label: 'Sous-P.', value: analysis?.performanceDistribution?.sousPerformance || 0, color: '#ef4444' },
  ];

  return (
    <DashboardLayout title="Vue Direction">
      <FeatureGate feature="ADVANCED_REPORTING" fallbackMessage="La Vue Direction est réservée au plan PRO">
        <div className="space-y-6 print:space-y-4" data-testid="executive-view-page">
          {/* Header */}
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 print:hidden">
            <div>
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-webtech-blue to-blue-700 flex items-center justify-center shadow-lg">
                  <Briefcase className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h1 className="text-2xl font-bold text-slate-900">Vue Direction</h1>
                  <p className="text-slate-500 text-sm flex items-center gap-2">
                    <Calendar className="w-4 h-4" />
                    Synthèse RH au {new Date().toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}
                  </p>
                </div>
              </div>
            </div>
            
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={handlePrint}>
                <Printer className="h-4 w-4 mr-2" />
                Imprimer
              </Button>
              <Button 
                size="sm" 
                className="bg-webtech-blue hover:bg-webtech-blue-hover text-white"
                onClick={handleExport}
                disabled={exporting}
              >
                {exporting ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Download className="h-4 w-4 mr-2" />
                )}
                {exporting ? 'Export...' : 'Export PDF'}
              </Button>
            </div>
          </div>

          {/* Print Header */}
          <div className="hidden print:block mb-8">
            <div className="flex items-center justify-between border-b-2 border-webtech-blue pb-4">
              <div>
                <h1 className="text-2xl font-bold text-webtech-blue">Webtech HR Engine™</h1>
                <p className="text-slate-600">Rapport de Direction</p>
              </div>
              <div className="text-right">
                <p className="text-sm text-slate-500">Généré le {new Date().toLocaleDateString('fr-FR')}</p>
                <p className="text-sm text-slate-500">{user?.firstName} {user?.lastName}</p>
              </div>
            </div>
          </div>

          {loading ? (
            <ExecutiveViewSkeleton />
          ) : (
            <>
              {/* Top KPIs */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <ExecutiveKPI 
                  label="Effectif total"
                  value={stats?.employees || 0}
                  subValue={`${stats?.departments || 0} départements`}
                  icon={Users}
                  color="bg-blue-500"
                  delay={0}
                />
                <ExecutiveKPI 
                  label="Score santé RH"
                  value={`${healthScore}%`}
                  subValue={healthScore >= 50 ? 'Satisfaisant' : 'À améliorer'}
                  icon={Activity}
                  color={healthScore >= 50 ? 'bg-green-500' : 'bg-amber-500'}
                  delay={100}
                />
                <ExecutiveKPI 
                  label="Risque turnover"
                  value={hrKPIs.turnoverRisk || 'N/A'}
                  subValue="Niveau actuel"
                  icon={AlertTriangle}
                  color={hrKPIs.turnoverRisk === 'élevé' ? 'bg-red-500' : hrKPIs.turnoverRisk === 'moyen' ? 'bg-amber-500' : 'bg-green-500'}
                  delay={200}
                />
                <ExecutiveKPI 
                  label="Engagement"
                  value={hrKPIs.engagementLevel || 'N/A'}
                  subValue="Niveau global"
                  icon={Zap}
                  color={hrKPIs.engagementLevel === 'élevé' ? 'bg-green-500' : hrKPIs.engagementLevel === 'moyen' ? 'bg-blue-500' : 'bg-amber-500'}
                  delay={300}
                />
              </div>

              {/* Main Content */}
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Executive Summary */}
                <Card className="lg:col-span-2">
                  <CardHeader className="pb-3">
                    <div className="flex items-center gap-2">
                      <Brain className="w-5 h-5 text-webtech-blue" />
                      <CardTitle className="text-lg">Synthèse IA</CardTitle>
                      <Badge className="bg-webtech-yellow/20 text-amber-700 text-xs">IA</Badge>
                    </div>
                  </CardHeader>
                  <CardContent>
                    {analysis?.executiveSummary ? (
                      <div className="space-y-4">
                        <p className="text-slate-700 leading-relaxed">
                          {analysis.executiveSummary}
                        </p>
                        <div className="flex items-center justify-between pt-3 border-t">
                          <span className="text-xs text-slate-400 flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            Dernière analyse : {data?.lastAnalysisDate ? new Date(data.lastAnalysisDate).toLocaleDateString('fr-FR') : 'N/A'}
                          </span>
                          <Link to="/ai-insights">
                            <Button variant="ghost" size="sm" className="text-webtech-blue">
                              Voir détails
                              <ChevronRight className="w-4 h-4 ml-1" />
                            </Button>
                          </Link>
                        </div>
                      </div>
                    ) : (
                      <div className="text-center py-8">
                        <Lightbulb className="w-12 h-12 mx-auto text-slate-300 mb-3" />
                        <p className="text-slate-500">Aucune analyse disponible</p>
                        <Link to="/ai-insights">
                          <Button variant="outline" size="sm" className="mt-3">
                            Lancer une analyse
                          </Button>
                        </Link>
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Health Score Gauge */}
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Shield className="w-5 h-5 text-webtech-blue" />
                      Santé RH Globale
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="flex flex-col items-center">
                    <HealthGauge score={healthScore} />
                    <div className="w-full mt-4 pt-4 border-t space-y-2">
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-slate-600">Compétences</span>
                        <span className={`font-medium ${hrKPIs.skillReadiness === 'élevé' ? 'text-green-600' : 'text-amber-600'}`}>
                          {hrKPIs.skillReadiness || 'N/A'}
                        </span>
                      </div>
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-slate-600">Engagement</span>
                        <span className={`font-medium ${hrKPIs.engagementLevel === 'élevé' ? 'text-green-600' : 'text-amber-600'}`}>
                          {hrKPIs.engagementLevel || 'N/A'}
                        </span>
                      </div>
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-slate-600">Turnover</span>
                        <span className={`font-medium ${hrKPIs.turnoverRisk === 'faible' ? 'text-green-600' : 'text-amber-600'}`}>
                          {hrKPIs.turnoverRisk || 'N/A'}
                        </span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Visualizations Row */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Radar Chart */}
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <PieChart className="w-5 h-5 text-webtech-blue" />
                      Vue d'ensemble RH
                    </CardTitle>
                    <CardDescription>
                      Performance globale par dimension
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <RadarChart data={radarData} size={220} />
                  </CardContent>
                </Card>

                {/* Performance Distribution */}
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <BarChart3 className="w-5 h-5 text-webtech-blue" />
                      Répartition Performance
                    </CardTitle>
                    <CardDescription>
                      Distribution des niveaux de performance
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="h-32">
                      <MiniBarChart data={performanceData} />
                    </div>
                    <div className="grid grid-cols-4 gap-2 mt-4 pt-4 border-t">
                      {performanceData.map((item, i) => (
                        <div key={i} className="text-center">
                          <p className="text-lg font-bold" style={{ color: item.color }}>
                            {item.value}%
                          </p>
                          <p className="text-[10px] text-slate-500">{item.label}</p>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Risks and Recommendations */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Key Risks */}
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <AlertTriangle className="w-5 h-5 text-amber-500" />
                      Risques Prioritaires
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {analysis?.keyRisks?.slice(0, 3).map((risk, idx) => (
                        <RiskItem 
                          key={idx}
                          risk={risk}
                          severity={idx === 0 ? 'high' : 'medium'}
                        />
                      )) || (
                        <p className="text-slate-500 text-sm text-center py-4">
                          Aucun risque majeur identifié
                        </p>
                      )}
                    </div>
                    {analysis?.keyRisks?.length > 3 && (
                      <Link to="/ai-insights">
                        <Button variant="ghost" size="sm" className="w-full mt-3 text-webtech-blue">
                          Voir tous les risques ({analysis.keyRisks.length})
                          <ArrowRight className="w-4 h-4 ml-2" />
                        </Button>
                      </Link>
                    )}
                  </CardContent>
                </Card>

                {/* Urgent Actions */}
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Target className="w-5 h-5 text-blue-500" />
                      Actions Recommandées
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {analysis?.urgentActions?.slice(0, 3).map((action, idx) => (
                        <div 
                          key={idx}
                          className="p-3 rounded-lg bg-blue-50 border border-blue-100 flex items-start gap-3"
                        >
                          <CheckCircle2 className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
                          <p className="text-sm text-blue-800">{action}</p>
                        </div>
                      )) || (
                        <p className="text-slate-500 text-sm text-center py-4">
                          Aucune action urgente requise
                        </p>
                      )}
                    </div>
                    {analysis?.strategicRecommendations?.length > 0 && (
                      <Link to="/ai-insights">
                        <Button variant="ghost" size="sm" className="w-full mt-3 text-webtech-blue">
                          Voir toutes les recommandations
                          <ArrowRight className="w-4 h-4 ml-2" />
                        </Button>
                      </Link>
                    )}
                  </CardContent>
                </Card>
              </div>

              {/* Key Strengths */}
              {analysis?.keyStrengths?.length > 0 && (
                <Card className="bg-gradient-to-br from-green-50 to-emerald-50 border-green-200">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-lg flex items-center gap-2 text-green-800">
                      <Award className="w-5 h-5" />
                      Points Forts de l'Organisation
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {analysis.keyStrengths.slice(0, 4).map((strength, idx) => (
                        <div 
                          key={idx}
                          className="flex items-start gap-2 p-2 bg-white/60 rounded-lg"
                        >
                          <CheckCircle2 className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                          <p className="text-sm text-green-800">{strength}</p>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Footer Actions */}
              <div className="flex items-center justify-between pt-4 border-t print:hidden">
                <p className="text-sm text-slate-400">
                  Rapport généré par Webtech HR Engine™ IA
                </p>
                <div className="flex items-center gap-2">
                  <Link to="/ai-insights">
                    <Button variant="outline" size="sm">
                      <Brain className="w-4 h-4 mr-2" />
                      Analyse détaillée
                    </Button>
                  </Link>
                  <Button 
                    size="sm" 
                    className="bg-webtech-blue hover:bg-webtech-blue-hover"
                    onClick={() => navigate('/dashboard')}
                  >
                    Retour au Dashboard
                  </Button>
                </div>
              </div>
            </>
          )}
        </div>
      </FeatureGate>
    </DashboardLayout>
  );
}
